'use client'

import { useEffect, useRef, useState } from 'react'
import { Snackbar } from '@/components/ui/Snackbar'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { cn } from '@/lib/utils/cn'
import AceLockScreen from '@/resources/images/ace-lockscreen.png'
import { useUserProfile } from '@/hooks/business/useUserProfile'
import { useInactivityTimeoutSetTimeout } from '@/components/utils/InactivityTimeoutProvider'
import { useHotkeys } from 'react-hotkeys-hook'

const employeeIdRegex = /^\d{6,8}$/

export type LockScreenProps = {
  onUnlock: () => unknown
  className?: string
}

export function LockScreen({ onUnlock, className }: LockScreenProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const validInput = employeeIdRegex.test(inputValue)
  const [error, setError] = useState<{ message: string; secondaryMessage: string } | undefined>(
    undefined
  )
  const user = useUserProfile()
  const setInactivityTimeout = useInactivityTimeoutSetTimeout()
  const router = useRouter()

  useEffect(() => {
    setInactivityTimeout(1_800_000)
  }, [setInactivityTimeout])

  useEffect(() => {
    if (user === undefined) {
      setInactivityTimeout(null)
      onUnlock()
      router.push('/home')
    }
  }, [onUnlock, router, setInactivityTimeout, user])

  useHotkeys('*', () => inputRef.current?.focus())

  return (
    <main className={cn('mx-auto p-8', className)}>
      <div className="flex">
        <Image
          src={AceLockScreen}
          alt="ACE Lockscreen image"
          className="hidden w-[450px] lg:block lg:w-[550px]"
        />
        <div className="h-[425px] w-[425px] rounded-lg border-2 border-[#B4C1CF] px-8 py-7 lg:h-[unset] lg:w-[550px] lg:rounded-l-none">
          <h1 className="mb-20 text-3xl font-bold">
            {user?.firstName !== undefined ? `Hi, ${user?.firstName}!` : 'Welcome to ACE!'}
          </h1>
          <h3 className="mb-3 text-2xl font-medium">Enter User ID to unlock</h3>
          <form
            onSubmit={async (event) => {
              event.preventDefault()
              setError(undefined)
              if (inputValue !== user?.id) {
                setError({
                  message: 'Invalid email/employee ID',
                  secondaryMessage: 'Please try again',
                })
              } else {
                setInactivityTimeout(null)
                onUnlock()
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-1">
              <label className="text-[#637E9C]" htmlFor="unlock">
                User ID
              </label>
              <input
                ref={inputRef}
                autoFocus
                autoComplete="off"
                required
                name="unlock"
                pattern="^\d{6,8}$"
                type="text"
                value={inputValue}
                onChange={(event) => {
                  setInputValue(event.target.value.toUpperCase().replace(/[^0-9]/g, ''))
                }}
                className="w-full rounded border border-[#637E9C] px-4 py-3"
              />
            </div>
            <button
              type="submit"
              className={cn(
                'w-full rounded bg-[#0860C4] px-4 py-3 font-bold text-white dark:bg-[#1A94FF]',
                !validInput && 'bg-[#B4C1CF]'
              )}
            >
              Unlock
            </button>
            {error !== undefined && (
              <Snackbar
                variant="error"
                message={error.secondaryMessage ?? []}
                heading={error.message}
                onClose={() => setError(undefined)}
              />
            )}
          </form>
        </div>
      </div>
    </main>
  )
}
